import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import InventoryIcon from "@mui/icons-material/Inventory";
import {
  findAllSubCategory,
  searchProductCategories,
  searchProductForStage,
} from "../../../../../api/inventoryService";
import {
  addStageProduct,
  brandsInProducts,
  productCategoryName,
  stageProductError,
  StageCategoryLink,
  StageProductLine,
} from "./stageProducts";

type Option = { value: string; label: string };

const labelClass =
  "block text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const inputClass =
  "w-full h-9 px-2.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all";
const headClass =
  "px-2.5 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400";
const menuPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed" as const,
};

/**
 * "Products used" on a flagged treatment stage: Category → Brand → Product,
 * quantity, several lines, no price. Stock leaves inventory when the stage
 * starts (backend). Once released the list stays editable: saving takes the
 * extra from stock and puts back what was removed.
 *
 * When the catalog ties the stage to a category (`link`), the category picker
 * is hidden and only products from the linked sub categories are offered, so
 * staff pick brand and product only.
 */
function StageProductsPicker({ lines, onChange, released, selectStyles, scope, link }: StageProductsPickerProps) {
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [picked, setPicked] = useState<any>(null);
  const [quantity, setQuantity] = useState("1");

  useEffect(() => {
    searchProductCategories("")
      .then((data) => setCategories(data as any[]))
      .catch((error) => console.error(error));
  }, []);

  const linkedSubCount = link?.subCategoryIds.length || 0;
  useEffect(() => {
    if (!linkedSubCount) return;
    findAllSubCategory()
      .then((data) => setSubCategories(data as any[]))
      .catch((error) => console.error(error));
  }, [linkedSubCount]);

  const linkKey = link ? `${link.categoryId}:${link.subCategoryIds.join(",")}` : "";
  const { treatmentId, typeId, subtypeId, stage } = scope || {};
  useEffect(() => {
    let alive = true;
    searchProductForStage({
      categoryId: linkKey ? "" : categoryId,
      scope: { treatmentId, typeId, subtypeId, stage },
    })
      .then((data) => {
        if (alive) setProducts(data as any[]);
      })
      .catch((error) => console.error(error));
    return () => {
      alive = false;
    };
  }, [categoryId, linkKey, treatmentId, typeId, subtypeId, stage]);

  // A stage switched to another link starts the pickers over.
  useEffect(() => {
    setCategoryId("");
    setBrandId("");
    setPicked(null);
  }, [linkKey]);

  const linkLabel = useMemo(() => {
    if (!link) return "";
    const category = categories.find((c) => String(c._id) === link.categoryId)?.categoryName || "the linked category";
    const subs = subCategories
      .filter((s) => link.subCategoryIds.includes(String(s._id)))
      .map((s) => s.subCategoryName)
      .filter(Boolean);
    return subs.length ? `${category}: ${subs.join(", ")}` : category;
  }, [link, categories, subCategories]);

  const categoryOptions: Option[] = useMemo(
    () => categories.map((c) => ({ value: String(c._id), label: c.categoryName || "Unnamed" })),
    [categories]
  );
  const brandOptions: Option[] = useMemo(
    () => brandsInProducts(products).map((b) => ({ value: b._id, label: b.brandName })),
    [products]
  );
  const productOptions = useMemo(
    () =>
      (brandId ? products.filter((p) => String(p?.brandId?._id) === brandId) : products).map((p) => ({
        value: String(p._id),
        label: p.productName || "Unnamed",
        product: p,
      })),
    [products, brandId]
  );

  const resetProduct = () => {
    setPicked(null);
    setQuantity("1");
  };

  const handleAdd = () => {
    const line: StageProductLine = {
      productId: picked?._id || "",
      productName: picked?.productName || "",
      brandName: picked?.brandId?.brandName || "",
      categoryName: productCategoryName(picked, categories),
      scale: picked?.scale,
      availableQuantity: picked?.availableQuantity,
      quantity: Number(quantity),
    };
    const already = lines.find((l) => String(l.productId) === String(line.productId))?.quantity || 0;
    // What the stage already took is out of stock, so only the extra must fit.
    const out = released?.find((l) => String(l.productId) === String(line.productId))?.quantity || 0;
    const error = stageProductError(line, already - out);
    if (error) {
      toast.error(error);
      return;
    }
    onChange(addStageProduct(lines, line));
    resetProduct();
  };

  const formatProductOption = (option: { label: string; product: any }) => (
    <div className="flex items-center justify-between gap-3 w-full">
      <span className="truncate">
        {option.label}
        {option.product?.brandId?.brandName && (
          <span className="text-slate-400"> · {option.product.brandId.brandName}</span>
        )}
      </span>
      <span className="shrink-0 text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">
        {option.product?.availableQuantity ?? 0} {option.product?.scale || ""} in stock
      </span>
    </div>
  );

  return (
    <div className="pt-1 border-t border-slate-100 dark:border-slate-700/80 space-y-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
        <InventoryIcon sx={{ fontSize: 14 }} className="text-indigo-500 dark:text-indigo-300" />
        Products used
        {released ? (
          <span className="font-normal normal-case text-emerald-600 dark:text-emerald-300">
            released from stock · changes are taken from or returned to stock on save
          </span>
        ) : (
          <span className="font-normal normal-case text-slate-400 dark:text-slate-500">
            · taken from stock when this stage starts
          </span>
        )}
      </div>

      {link && (
        <p className="text-[10px] text-slate-500 dark:text-slate-400">
          Products from <span className="font-semibold text-slate-700 dark:text-slate-200">{linkLabel}</span>, set in
          the treatment catalog.
        </p>
      )}

      <div className="grid grid-cols-12 gap-2.5 items-end">
        {!link && (
        <div className="col-span-12 sm:col-span-6 lg:col-span-3 min-w-0">
          <label className={labelClass}>Category</label>
          <Select
            isClearable
            isSearchable
            placeholder="All categories"
            options={categoryOptions}
            value={categoryOptions.find((o) => o.value === categoryId) ?? null}
            onChange={(option: Option | null) => {
              setCategoryId(option?.value || "");
              setBrandId("");
              resetProduct();
            }}
            styles={selectStyles}
            classNamePrefix="react-select"
            {...menuPortalProps}
          />
        </div>
        )}
        <div className={`col-span-12 sm:col-span-6 ${link ? "lg:col-span-4" : "lg:col-span-3"} min-w-0`}>
          <label className={labelClass}>Brand</label>
          <Select
            isClearable
            isSearchable
            placeholder="All brands"
            options={brandOptions}
            value={brandOptions.find((o) => o.value === brandId) ?? null}
            onChange={(option: Option | null) => {
              setBrandId(option?.value || "");
              resetProduct();
            }}
            styles={selectStyles}
            classNamePrefix="react-select"
            {...menuPortalProps}
          />
        </div>
        <div className={`col-span-12 sm:col-span-6 ${link ? "lg:col-span-5" : "lg:col-span-4"} min-w-0`}>
          <label className={labelClass}>Product</label>
          <Select
            isSearchable
            placeholder="Select product"
            noOptionsMessage={() => "No products"}
            options={productOptions}
            formatOptionLabel={formatProductOption as any}
            value={productOptions.find((o) => o.value === String(picked?._id)) ?? null}
            onChange={(option: any) => setPicked(option?.product || null)}
            styles={selectStyles}
            classNamePrefix="react-select"
            {...menuPortalProps}
          />
        </div>
        <div className="col-span-6 sm:col-span-3 lg:col-span-1">
          <label className={labelClass}>Qty</label>
          <input
            value={quantity}
            type="number"
            min={1}
            onChange={(e) => setQuantity(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="col-span-6 sm:col-span-3 lg:col-span-1">
          <button
            type="button"
            onClick={handleAdd}
            disabled={!picked}
            className="w-full h-9 text-[11px] rounded-lg bg-primary text-white font-semibold hover:shadow-md hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      </div>

      {lines.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-700/40">
                <th className={headClass}>Product</th>
                <th className={headClass}>Brand</th>
                <th className={headClass}>Category</th>
                <th className={`${headClass} text-right`}>Qty</th>
                <th className={headClass} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {lines.map((line, i) => (
                <tr key={`${line.productId}-${i}`}>
                  <td className="px-2.5 py-2 font-medium text-slate-800 dark:text-slate-100">
                    {line.productName || "Product"}
                  </td>
                  <td className="px-2.5 py-2 text-slate-600 dark:text-slate-300">{line.brandName || "-"}</td>
                  <td className="px-2.5 py-2 text-slate-600 dark:text-slate-300">{line.categoryName || "-"}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-slate-800 dark:text-slate-100 whitespace-nowrap">
                    {line.quantity} {line.scale || ""}
                  </td>
                  <td className="px-2.5 py-2 text-right w-px whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onChange(lines.filter((_, j) => j !== i))}
                      className="text-[10px] font-medium text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[10px] text-slate-400 dark:text-slate-500">
          No products added. The stage can still be saved without any.
        </p>
      )}
    </div>
  );
}

export default StageProductsPicker;

interface StageProductsPickerProps {
  lines: StageProductLine[];
  onChange: (lines: StageProductLine[]) => void;
  /** The saved lines of a stage that has released (already out of stock); unset before release */
  released?: StageProductLine[];
  /** react-select styles of the surrounding form, so the pickers match it */
  selectStyles?: any;
  /** The sheet line's catalog ids and stage, sent so the backend applies the stage's category link */
  scope?: { treatmentId?: string; typeId?: string; subtypeId?: string; stage?: string };
  /** The stage's catalog category link; hides the category picker */
  link?: StageCategoryLink | null;
}
